import { useEffect, useState } from "react";
import api from "@/lib/api";
import NegotiationModal from "@/components/NegotiationModal";
import { MessageSquare, ShoppingBag, TrendingDown, IndianRupee } from "lucide-react";

/**
 * Lists the current user's negotiations (as buyer or seller).
 * Optional `adminMode` prop to fetch ALL for admin tab.
 */
export default function MyNegotiations({ adminMode = false }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    const url = adminMode ? "/negotiations/admin/all" : "/negotiations/mine";
    api.get(url)
      .then(({ data }) => setItems(data))
      .catch((err) => console.warn(err))
      .finally(() => setLoading(false));
    if (adminMode) {
      api.get("/negotiations/admin/stats")
        .then(({ data }) => setStats(data))
        .catch(() => {});
    }
  };

  useEffect(() => { load(); }, [adminMode]);

  return (
    <div className="space-y-4" data-testid="nego-list-wrap">
      {adminMode && stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="bargain-stats">
          <div className="bg-white border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase text-black/60">TOTAL</p>
            <p className="font-display text-2xl leading-none">{stats.total}</p>
          </div>
          <div className="bg-[#F4F4F0] border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase text-black/60">OPEN / ACCEPTED / REJECTED / EXPIRED</p>
            <p className="font-mono-b text-sm mt-1">{stats.by_status?.open || 0} / <b>{stats.by_status?.accepted || 0}</b> / {stats.by_status?.rejected || 0} / {stats.by_status?.expired || 0}</p>
          </div>
          <div className="bg-black text-[#D4FF00] border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase opacity-70">OFFERS MADE</p>
            <p className="font-display text-2xl leading-none">{stats.total_offers}</p>
          </div>
          <div className="bg-[#D4FF00] border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" /> AVG DISCOUNT</p>
            <p className="font-display text-2xl leading-none">{stats.avg_discount_pct}%</p>
          </div>
          <div className="bg-[#FF007F] text-white border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase">SAVED BY BUYERS</p>
            <p className="font-display text-2xl leading-none">₹{stats.total_saved_by_buyers}</p>
          </div>
          <div className="bg-white border-2 border-black brutal-shadow-sm p-3">
            <p className="font-mono-b text-[10px] font-bold uppercase inline-flex items-center gap-1"><IndianRupee className="w-3 h-3" /> GMV VIA BARGAINS</p>
            <p className="font-display text-2xl leading-none">₹{stats.gmv_from_bargains}</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono-b p-6" data-testid="nego-loading">Loading negotiations...</p>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="nego-empty">
          <MessageSquare className="w-8 h-8 mx-auto opacity-40" />
          <p className="mt-2">No bargain chats yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="nego-list">
          {items.map((n) => (
        <button
          key={n.negotiation_id}
          onClick={() => setOpenId(n.negotiation_id)}
          data-testid={`nego-card-${n.negotiation_id}`}
          className="bg-white border-2 border-black brutal-shadow-sm p-3 text-left hover-nudge flex gap-3"
        >
          {n.product_image ? (
            <img src={n.product_image} alt="" className="w-16 h-16 object-cover border-2 border-black shrink-0" />
          ) : (
            <div className="w-16 h-16 border-2 border-black bg-[#F4F4F0] shrink-0 flex items-center justify-center"><ShoppingBag className="w-4 h-4" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2">
              <p className="font-display text-lg leading-tight truncate">{n.product_name}</p>
              <div className="flex items-center gap-1 shrink-0">
                {n.unread_for_me && (
                  <span data-testid={`nego-unread-${n.negotiation_id}`} className="w-2 h-2 rounded-full bg-[#FF007F] animate-pulse" title="unread" />
                )}
                <span className={`sticker text-[9px] shrink-0 ${n.status === "accepted" ? "" : n.status === "rejected" || n.status === "expired" ? "pink" : "orange"}`}>{n.status.toUpperCase()}</span>
              </div>
            </div>
            <p className="font-mono-b text-[11px] mt-1">List <b>₹{n.list_price}</b>{n.accepted_price && <> · Deal <b>₹{n.accepted_price}</b></>}</p>
            <p className="font-mono-b text-[10px] text-black/60 mt-1">
              {(n.messages || []).length} message(s) · {new Date(n.updated_at).toLocaleString()}
            </p>
          </div>
        </button>
      ))}
        </div>
      )}
      {openId && (
        <NegotiationModal
          negotiationId={openId}
          onClose={() => { setOpenId(null); load(); }}
          onAccepted={() => load()}
        />
      )}
    </div>
  );
}
